import type Phaser from 'phaser';

import { SHEFIRA_SILHOUETTE } from './silhouette.js';

const SILHOUETTE_TEXTURE = 'cinematic.shefira-silhouette';
const SHADOW_COLOUR = 0x0a0a12;

/**
 * One frame of Shefira's shadow at one scale, resampled nearest-neighbour
 * into a texture of its own, once: a scaled sprite would put its edges
 * between world pixels (CLAUDE.md rule 6), a resampled one keeps every
 * pixel whole. Shared by her scenes and her fight. Undefined when the
 * atlas lacks the frame.
 */
export function shadowTexture(
  scene: Phaser.Scene,
  sheet: { atlas: string; prefix: string },
  frame: number,
  scale: number,
): string | undefined {
  const key = `cinematic.shadow/${String(frame)}/${String(Math.round(scale * 10))}`;
  const textures = scene.textures;
  if (textures.exists(key)) return key;
  if (!textures.exists(sheet.atlas)) return undefined;
  const name = `${sheet.prefix}/${String(frame)}`;
  const atlas = textures.get(sheet.atlas);
  // Asked for a frame it lacks, Phaser hands back the whole sheet.
  if (!atlas.has(name)) return undefined;
  const source = atlas.get(name);
  const width = Math.max(1, Math.round(source.cutWidth * scale));
  const height = Math.max(1, Math.round(source.cutHeight * scale));
  const canvas = textures.createCanvas(key, width, height);
  if (canvas === null) return undefined;
  const context = canvas.getContext();
  context.imageSmoothingEnabled = false;
  context.drawImage(
    source.source.image as CanvasImageSource,
    source.cutX,
    source.cutY,
    source.cutWidth,
    source.cutHeight,
    0,
    0,
    width,
    height,
  );
  canvas.refresh();
  return key;
}

/** The stand-in silhouette drawn by code, pixel by pixel into a texture once: her shadow without her art. */
export function silhouetteTexture(scene: Phaser.Scene): string {
  if (scene.textures.exists(SILHOUETTE_TEXTURE)) return SILHOUETTE_TEXTURE;
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(SHADOW_COLOUR, 1);
  SHEFIRA_SILHOUETTE.forEach((row, y) => {
    for (let x = 0; x < row.length; x += 1) if (row[x] === '#') graphics.fillRect(x, y, 1, 1);
  });
  const width = Math.max(...SHEFIRA_SILHOUETTE.map((row) => row.length));
  graphics.generateTexture(SILHOUETTE_TEXTURE, width, SHEFIRA_SILHOUETTE.length);
  graphics.destroy();
  return SILHOUETTE_TEXTURE;
}
